
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown, Users, Zap, Target, Clock, RotateCcw, Activity } from 'lucide-react';
import { NorthStarMetrics } from '@/services/analytics/northStarMetrics';

interface NorthStarMetricsSectionProps {
  metrics: NorthStarMetrics | null;
  isLoading: boolean;
}

const NorthStarMetricsSection: React.FC<NorthStarMetricsSectionProps> = ({ metrics, isLoading }) => {
  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return Math.round(num).toString();
  };

  const formatPercentage = (num: number): string => {
    return `${num.toFixed(1)}%`;
  };

  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) {
      return `${Math.round(minutes)}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const getTrendIcon = (growth: number) => {
    if (growth > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (growth < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Activity className="h-4 w-4 text-gray-500" />;
  };

  const getTrendColor = (growth: number): string => {
    if (growth > 0) return 'text-green-600';
    if (growth < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">North Star Metrics</h2>
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-gray-200 rounded w-full"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-8">
        <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
        <p className="text-muted-foreground">North Star metrics will appear once there's sufficient user activity.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
          <Target className="h-6 w-6 text-primary" />
          North Star Metrics
        </h2>
        <p className="text-muted-foreground">
          Key performance indicators driving user value and engagement
        </p>
      </div>

      {/* Primary North Star Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* WPAU - Main North Star */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Weekly Prompt-Activated Users (WPAU)
            </CardTitle>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-primary">{formatNumber(metrics.wpau)}</span>
              <div className="flex items-center gap-1">
                {getTrendIcon(metrics.wpauGrowth)}
                <span className={`text-sm font-medium ${getTrendColor(metrics.wpauGrowth)}`}>
                  {metrics.wpauGrowth > 0 ? '+' : ''}{formatPercentage(metrics.wpauGrowth)}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Users who copy or save prompts this week
            </CardDescription>
            <Progress value={Math.min(metrics.wpau / 100 * 100, 100)} className="mt-2" />
          </CardContent>
        </Card>

        {/* WPE - Secondary North Star */}
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-blue-600" />
              Weekly Prompt Executions (WPE)
            </CardTitle>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-blue-600">{formatNumber(metrics.wpe)}</span>
              <div className="flex items-center gap-1">
                {getTrendIcon(metrics.wpeGrowth)}
                <span className={`text-sm font-medium ${getTrendColor(metrics.wpeGrowth)}`}>
                  {metrics.wpeGrowth > 0 ? '+' : ''}{formatPercentage(metrics.wpeGrowth)}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Total prompt copies and executions this week
            </CardDescription>
            <Progress value={Math.min(metrics.wpe / 1000 * 100, 100)} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Supporting Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* First Session Activation */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">First Session Activation</CardTitle>
            <div className="text-2xl font-bold">{formatPercentage(metrics.firstSessionActivation)}</div>
          </CardHeader>
          <CardContent>
            <CardDescription>% of signups that interact with prompts</CardDescription>
            <Progress value={metrics.firstSessionActivation} className="mt-2" />
          </CardContent>
        </Card>

        {/* Time to First Prompt */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Time to First Prompt
            </CardTitle>
            <div className="text-2xl font-bold">{formatMinutes(metrics.timeToFirstPrompt)}</div>
          </CardHeader>
          <CardContent>
            <CardDescription>Average time from signup to first interaction</CardDescription>
          </CardContent>
        </Card>

        {/* Day 7 Retention */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />
              Day 7 Retention
            </CardTitle>
            <div className="text-2xl font-bold">{formatPercentage(metrics.day7Retention)}</div>
          </CardHeader>
          <CardContent>
            <CardDescription>% of activated users still active after 7 days</CardDescription>
            <Progress value={metrics.day7Retention} className="mt-2" />
          </CardContent>
        </Card>

        {/* WAU/MAU Ratio */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">WAU/MAU Ratio</CardTitle>
            <div className="text-2xl font-bold">{formatPercentage(metrics.wauMauRatio)}</div>
          </CardHeader>
          <CardContent>
            <CardDescription>Weekly vs Monthly active users</CardDescription>
            <Progress value={metrics.wauMauRatio} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">PromptLab Sessions</CardTitle>
            <div className="text-xl font-bold">{formatNumber(metrics.promptlabSessions)}</div>
          </CardHeader>
          <CardContent>
            <CardDescription>Unique users with PromptLab activity this week</CardDescription>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">New Prompts Published</CardTitle>
            <div className="text-xl font-bold">{metrics.newPromptsPublished}</div>
          </CardHeader>
          <CardContent>
            <CardDescription>New approved prompts this week</CardDescription>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Prompt Utilization</CardTitle>
            <div className="text-xl font-bold">{formatPercentage(metrics.promptUtilization)}</div>
          </CardHeader>
          <CardContent>
            <CardDescription>% of prompts used at least once (last 30 days)</CardDescription>
            <Progress value={metrics.promptUtilization} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* Performance Indicators */}
      <div className="flex flex-wrap gap-2 justify-center">
        <Badge variant={metrics.wpau > 50 ? "default" : "secondary"}>
          {metrics.wpau > 50 ? "🎯 Strong WPAU" : "📈 Growing WPAU"}
        </Badge>
        <Badge variant={metrics.firstSessionActivation > 60 ? "default" : "secondary"}>
          {metrics.firstSessionActivation > 60 ? "⚡ High Activation" : "🔄 Improving Activation"}
        </Badge>
        <Badge variant={metrics.day7Retention > 50 ? "default" : "secondary"}>
          {metrics.day7Retention > 50 ? "🔒 Strong Retention" : "📊 Building Retention"}
        </Badge>
        <Badge variant={metrics.wauMauRatio > 25 ? "default" : "secondary"}>
          {metrics.wauMauRatio > 25 ? "🔥 High Engagement" : "📱 Growing Engagement"}
        </Badge>
      </div>
    </div>
  );
};

export default NorthStarMetricsSection;
